import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  StaticFieldForm,
  StaticFieldFormData,
} from "./fieldForms/StaticFieldForm";
import {
  ComputedFieldForm,
  ComputedFieldFormData,
} from "./fieldForms/ComputedFieldForm";
import {
  AttributeTableFieldForm,
  AttributeTableFieldFormData,
} from "./fieldForms/AttributeTableFieldForm";
import {
  SampleTableFieldForm,
  SampleTableFieldFormData,
} from "./fieldForms/SampleTableFieldForm";
import {
  DetailTestingTableFieldForm,
  DetailTestingTableFieldFormData,
} from "./fieldForms/DetailTestingTableFieldForm";
import {
  CitationAssetSheetFieldForm,
  CitationAssetSheetFieldFormData,
} from "./fieldForms/CitationAssetSheetFieldForm";
import {
  ComputedTableFieldForm,
  ComputedTableFieldFormData,
} from "./fieldForms/ComputedTableFieldForm";
import {
  FieldType,
  FieldTypeEnum,
  DraftTemplateField,
  TemplateType,
  SOURCES_BY_TEMPLATE_TYPE,
} from "../../models/workPaperTemplate";

interface AddFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (
    field: Omit<DraftTemplateField, "id" | "fieldOrder" | "isNew">,
  ) => void;
  templateType: TemplateType;
}

function getDefaultStaticField(
  templateType: TemplateType,
): StaticFieldFormData {
  return {
    sheetName: "Sheet1",
    cellRow: 1,
    cellColumn: "A",
    source: SOURCES_BY_TEMPLATE_TYPE[templateType][0],
    sourceSelector: "name",
  };
}

const DEFAULT_COMPUTED_FIELD: ComputedFieldFormData = {
  sheetName: "Sheet1",
  cellRow: 1,
  cellColumn: "A",
  context: "test_results",
  instructions: "",
};

const DEFAULT_ATTRIBUTE_TABLE: AttributeTableFieldFormData = {
  sheetName: "Sheet1",
  startRow: 1,
  attributeNameColumn: "A",
  attributeOutcomeColumn: "B",
  attributeOutcomeDescriptionColumn: null,
  descriptionInstructions: null,
  evidenceSummaryColumn: null,
  concatEvidenceWithDescription: false,
};

const DEFAULT_SAMPLE_TABLE: SampleTableFieldFormData = {
  sheetName: "Sheet1",
  startRow: 2,
  attributeHeaderRow: 1,
  sampleNumberColumn: "A",
  outcomeStartColumn: "B",
};

const DEFAULT_DETAIL_TESTING_TABLE: DetailTestingTableFieldFormData = {
  sheetNamingMode: "multi",
  sheetNamePrefix: "Testing",
  sheetName: undefined,
  sheetNameTemplate: "{prefix}_{procedure_name}",
  startRow: 2,
  headerRow: 1,
  columnStart: "A",
  includeSampleAttributes: true,
  includeProcedureIds: null,
  colors: null,
};

const DEFAULT_CITATION_ASSET_SHEET: CitationAssetSheetFieldFormData = {
  sheetNamePrefix: "Citations",
  sheetNameTemplate: "{prefix}_{procedure_name}",
  organization: "by_sample",
  startRow: 1,
  imagesPerRow: 1,
  includeAnnotations: true,
  includeNonAttributeTests: false,
  includeProcedureIds: null,
  truncateEvalColumnStepsTaken: false,
};

const DEFAULT_COMPUTED_TABLE: ComputedTableFieldFormData = {
  sheetName: "Sheet1",
  startRow: 2,
  headerRow: 1,
  columnStart: "A",
  context: "test_results",
  instructions: "",
  columns: [],
};

export function AddFieldDialog({
  open,
  onOpenChange,
  onAdd,
  templateType,
}: AddFieldDialogProps) {
  const [fieldName, setFieldName] = useState<string>("");
  const [fieldType, setFieldType] = useState<FieldType>("static");
  const [staticFieldData, setStaticFieldData] = useState<StaticFieldFormData>(
    () => getDefaultStaticField(templateType),
  );
  const [computedFieldData, setComputedFieldData] =
    useState<ComputedFieldFormData>(DEFAULT_COMPUTED_FIELD);
  const [attributeTableData, setAttributeTableData] =
    useState<AttributeTableFieldFormData>(DEFAULT_ATTRIBUTE_TABLE);
  const [sampleTableData, setSampleTableData] =
    useState<SampleTableFieldFormData>(DEFAULT_SAMPLE_TABLE);
  const [detailTestingTableData, setDetailTestingTableData] =
    useState<DetailTestingTableFieldFormData>(DEFAULT_DETAIL_TESTING_TABLE);
  const [citationAssetSheetData, setCitationAssetSheetData] =
    useState<CitationAssetSheetFieldFormData>(DEFAULT_CITATION_ASSET_SHEET);
  const [computedTableData, setComputedTableData] =
    useState<ComputedTableFieldFormData>(DEFAULT_COMPUTED_TABLE);

  const handleAdd = () => {
    const baseField = { name: fieldName, fieldType };

    let fieldData: Omit<DraftTemplateField, "id" | "fieldOrder" | "isNew">;

    switch (fieldType) {
      case "static":
        fieldData = {
          ...baseField,
          staticField: {
            id: "", // Will be set by the hook
            sheetName: staticFieldData.sheetName,
            cellAddress: {
              row: staticFieldData.cellRow,
              column: staticFieldData.cellColumn,
            },
            source: staticFieldData.source,
            sourceSelector: staticFieldData.sourceSelector,
          },
          computedField: null,
          attributeSummaryTable: null,
          sampleSummaryTable: null,
          detailTestingTable: null,
          citationAssetSheet: null,
          computedTableField: null,
        };
        break;

      case "dynamic":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: {
            id: "", // Will be set by the hook
            sheetName: computedFieldData.sheetName,
            cellAddress: {
              row: computedFieldData.cellRow,
              column: computedFieldData.cellColumn,
            },
            context: computedFieldData.context,
            instructions: computedFieldData.instructions,
          },
          attributeSummaryTable: null,
          sampleSummaryTable: null,
          detailTestingTable: null,
          citationAssetSheet: null,
          computedTableField: null,
        };
        break;

      case "attribute_summary_table":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: null,
          attributeSummaryTable: attributeTableData,
          sampleSummaryTable: null,
          detailTestingTable: null,
          citationAssetSheet: null,
          computedTableField: null,
        };
        break;

      case "sample_summary_table":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: null,
          attributeSummaryTable: null,
          sampleSummaryTable: sampleTableData,
          detailTestingTable: null,
          citationAssetSheet: null,
          computedTableField: null,
        };
        break;

      case "detail_testing_table":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: null,
          attributeSummaryTable: null,
          sampleSummaryTable: null,
          detailTestingTable: {
            // XOR: only include the active mode's field
            sheetName:
              detailTestingTableData.sheetNamingMode === "single"
                ? detailTestingTableData.sheetName
                : undefined,
            sheetNamePrefix:
              detailTestingTableData.sheetNamingMode === "multi"
                ? detailTestingTableData.sheetNamePrefix
                : undefined,
            sheetNameTemplate:
              detailTestingTableData.sheetNameTemplate ??
              "{prefix}_{procedure_name}",
            startRow: detailTestingTableData.startRow ?? 2,
            headerRow: detailTestingTableData.headerRow ?? 1,
            columnStart: detailTestingTableData.columnStart ?? "A",
            includeSampleAttributes:
              detailTestingTableData.includeSampleAttributes ?? true,
            includeProcedureIds: detailTestingTableData.includeProcedureIds,
            colors: detailTestingTableData.colors ?? null,
          },
          citationAssetSheet: null,
          computedTableField: null,
        };
        break;

      case "citation_asset_sheet":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: null,
          attributeSummaryTable: null,
          sampleSummaryTable: null,
          detailTestingTable: null,
          citationAssetSheet: {
            sheetNamePrefix: citationAssetSheetData.sheetNamePrefix,
            sheetNameTemplate:
              citationAssetSheetData.sheetNameTemplate ??
              "{prefix}_{procedure_name}",
            organization: citationAssetSheetData.organization ?? "by_sample",
            startRow: citationAssetSheetData.startRow ?? 1,
            imagesPerRow: citationAssetSheetData.imagesPerRow ?? 1,
            includeAnnotations:
              citationAssetSheetData.includeAnnotations ?? true,
            includeNonAttributeTests:
              citationAssetSheetData.includeNonAttributeTests ?? false,
            includeProcedureIds: citationAssetSheetData.includeProcedureIds,
            truncateEvalColumnStepsTaken:
              citationAssetSheetData.truncateEvalColumnStepsTaken ?? false,
          },
          computedTableField: null,
        };
        break;

      case "computed_table":
        fieldData = {
          ...baseField,
          staticField: null,
          computedField: null,
          attributeSummaryTable: null,
          sampleSummaryTable: null,
          detailTestingTable: null,
          citationAssetSheet: null,
          computedTableField: {
            id: "", // Will be set by the hook
            sheetName: computedTableData.sheetName,
            startRow: computedTableData.startRow ?? 2,
            headerRow: computedTableData.headerRow ?? 1,
            columnStart: computedTableData.columnStart ?? "A",
            context: computedTableData.context,
            instructions: computedTableData.instructions,
            columns: computedTableData.columns.map((col) => ({
              id: col.id,
              name: col.name,
              dataType: col.dataType,
              instructions: col.instructions,
              columnOrder: col.columnOrder,
            })),
          },
        };
        break;

      default:
        return;
    }

    onAdd(fieldData);
    onOpenChange(false);

    // Reset form
    setFieldName("");
    setFieldType("static");
    setStaticFieldData(getDefaultStaticField(templateType));
    setComputedFieldData(DEFAULT_COMPUTED_FIELD);
    setAttributeTableData(DEFAULT_ATTRIBUTE_TABLE);
    setSampleTableData(DEFAULT_SAMPLE_TABLE);
    setDetailTestingTableData(DEFAULT_DETAIL_TESTING_TABLE);
    setCitationAssetSheetData(DEFAULT_CITATION_ASSET_SHEET);
    setComputedTableData(DEFAULT_COMPUTED_TABLE);
  };

  const canAdd = () => {
    if (!fieldName.trim()) {
      return false;
    }

    switch (fieldType) {
      case "static":
        return (
          staticFieldData.sheetName &&
          staticFieldData.cellColumn &&
          staticFieldData.sourceSelector
        );
      case "dynamic":
        return (
          computedFieldData.sheetName &&
          computedFieldData.cellColumn &&
          computedFieldData.instructions
        );
      case "attribute_summary_table":
        return (
          attributeTableData.sheetName &&
          attributeTableData.attributeNameColumn &&
          attributeTableData.attributeOutcomeColumn
        );
      case "sample_summary_table":
        return (
          sampleTableData.sheetName &&
          sampleTableData.sampleNumberColumn &&
          sampleTableData.outcomeStartColumn
        );
      case "detail_testing_table":
        if (detailTestingTableData.sheetNamingMode === "multi") {
          return detailTestingTableData.sheetNamePrefix.trim().length > 0;
        } else {
          return (detailTestingTableData.sheetName?.trim().length ?? 0) > 0;
        }
      case "citation_asset_sheet":
        return citationAssetSheetData.sheetNamePrefix.trim().length > 0;
      case "computed_table":
        return (
          computedTableData.sheetName.trim().length > 0 &&
          computedTableData.instructions.trim().length > 0 &&
          computedTableData.columns.length > 0 &&
          computedTableData.columns.every((col) => col.name.trim().length > 0)
        );
      default:
        return false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Field</DialogTitle>
          <DialogDescription>
            Configure a new field for your workpaper template
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="fieldName">Field Name</Label>
            <Input
              id="fieldName"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="e.g., Control Name, Test Summary, Sample Results"
              maxLength={255}
            />
            <p className="text-sm text-muted-foreground">
              A descriptive name to identify this field
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fieldType">Field Type</Label>
            <Select
              value={fieldType}
              onValueChange={(value) =>
                setFieldType(FieldTypeEnum.parse(value))
              }
            >
              <SelectTrigger id="fieldType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="static">
                  {templateType === "work_step"
                    ? "Static Field (Work Step)"
                    : "Static Field (Control Data)"}
                </SelectItem>
                <SelectItem value="dynamic">AI-Generated Field</SelectItem>
                <SelectItem value="attribute_summary_table">
                  Attribute Summary Table
                </SelectItem>
                <SelectItem value="sample_summary_table">
                  Sample Summary Table
                </SelectItem>
                <SelectItem value="detail_testing_table">
                  Detail Testing Table
                </SelectItem>
                <SelectItem value="citation_asset_sheet">
                  Citation Asset Sheet
                </SelectItem>
                <SelectItem value="computed_table">
                  AI-Generated Table
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-4">
            {fieldType === "static" && (
              <StaticFieldForm
                value={staticFieldData}
                onChange={setStaticFieldData}
                allowedSources={SOURCES_BY_TEMPLATE_TYPE[templateType]}
              />
            )}
            {fieldType === "dynamic" && (
              <ComputedFieldForm
                value={computedFieldData}
                onChange={setComputedFieldData}
              />
            )}
            {fieldType === "attribute_summary_table" && (
              <AttributeTableFieldForm
                value={attributeTableData}
                onChange={setAttributeTableData}
              />
            )}
            {fieldType === "sample_summary_table" && (
              <SampleTableFieldForm
                value={sampleTableData}
                onChange={setSampleTableData}
              />
            )}
            {fieldType === "detail_testing_table" && (
              <DetailTestingTableFieldForm
                value={detailTestingTableData}
                onChange={setDetailTestingTableData}
              />
            )}
            {fieldType === "citation_asset_sheet" && (
              <CitationAssetSheetFieldForm
                value={citationAssetSheetData}
                onChange={setCitationAssetSheetData}
              />
            )}
            {fieldType === "computed_table" && (
              <ComputedTableFieldForm
                value={computedTableData}
                onChange={setComputedTableData}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!canAdd()}>
            Add Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
